<template>
  <div
    class="max-w-4xl mx-auto p-6 create-tenant"
    :class="{ 'dark-theme': isDarkTheme }"
  >
    <!-- Breadcrumb -->
    <nav class="mb-6">
      <router-link
        to="/platform/tenants"
        class="inline-flex items-center gap-2 text-orange-500
               hover:text-pink-500 transition-colors"
      >
        <i class="pi pi-arrow-left"></i>
        Back to Tenants
      </router-link>
    </nav>

    <!-- Page Header -->
    <header class="mb-8">
      <h1 class="text-2xl font-bold text-gray-800">Create New Tenant</h1>
      <p class="text-gray-500 mt-1">
        Set up a new organization with their own isolated environment
      </p>
    </header>

    <!-- Creation Mode Toggle -->
    <SelectButton
      v-model="mode"
      :options="modeOptions"
      optionLabel="label"
      optionValue="value"
      aria-labelledby="mode-toggle"
      class="mb-6"
    />

    <!-- Form -->
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Basic Information -->
      <Card>
        <template #title>
          <div class="flex items-center gap-2 text-gray-800">
            <i class="pi pi-building text-orange-500"></i>
            Basic Information
          </div>
        </template>
        <template #content>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-2">
              <label for="name" class="font-medium text-gray-700">Organization Name *</label>
              <InputText
                id="name"
                v-model="form.name"
                placeholder="e.g., Acme Corporation"
                :class="{ 'p-invalid': errors.name }"
                @blur="generateSlug"
              />
              <small v-if="errors.name" class="text-red-500">{{ errors.name }}</small>
            </div>

            <div class="flex flex-col gap-2">
              <label for="slug" class="font-medium text-gray-700">Tenant Slug *</label>
              <InputText
                id="slug"
                v-model="form.slug"
                placeholder="e.g., acme"
                :class="{ 'p-invalid': errors.slug }"
              />
              <Tag severity="info" class="w-fit">{{ form.slug || 'tenant' }}.omind.com</Tag>
              <small v-if="errors.slug" class="text-red-500">{{ errors.slug }}</small>
              <small class="text-gray-500">
                This will be used for the subdomain and database name
              </small>
            </div>

            <div class="flex flex-col gap-2 md:col-span-2">
              <label for="reason" class="font-medium text-gray-700">
                Reason for creation *
              </label>
              <Textarea
                id="reason"
                v-model="form.reason"
                rows="2"
                maxlength="200"
                placeholder="Short justification for the audit log (e.g. new customer onboarding)"
                class="w-full"
                :class="{ 'p-invalid': errors.reason }"
              />
              <small v-if="errors.reason" class="text-red-500">{{ errors.reason }}</small>
              <small class="text-gray-500">
                Required for compliance; stored with the tenant audit entry (max 200 characters).
              </small>
            </div>
          </div>
        </template>
      </Card>

      <!-- Admin Configuration -->
      <Card>
        <template #title>
          <div class="flex items-center gap-2 text-gray-800">
            <i class="pi pi-user text-orange-500"></i>
            Admin Configuration
          </div>
        </template>
        <template #content>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-2">
              <label for="adminEmail" class="font-medium text-gray-700">
                Primary Admin Email *
              </label>
              <InputText
                id="adminEmail"
                v-model="form.adminEmail"
                type="email"
                placeholder="admin@company.com"
                :class="{ 'p-invalid': errors.adminEmail }"
              />
              <small v-if="errors.adminEmail" class="text-red-500">{{ errors.adminEmail }}</small>
              <small class="text-gray-500">This user will have admin access to the tenant</small>
            </div>

            <div class="flex flex-col gap-2">
              <label for="additionalAdmins" class="font-medium text-gray-700">
                Additional Admin Emails
              </label>
              <InputText
                id="additionalAdmins"
                v-model="form.additionalAdminsInput"
                placeholder="admin2@company.com, admin3@company.com"
              />
              <small class="text-gray-500">Comma-separated list (optional)</small>
            </div>

            <div class="flex flex-col gap-2">
              <label for="superuserEmails" class="font-medium text-gray-700">
                Superuser Emails
              </label>
              <InputText
                id="superuserEmails"
                v-model="form.superuserEmailsInput"
                placeholder="superuser@company.com"
              />
              <small class="text-gray-500">Comma-separated list (optional)</small>
            </div>
          </div>
        </template>
      </Card>

      <!-- Plan & Configuration -->
      <Card>
        <template #title>
          <div class="flex items-center gap-2 text-gray-800">
            <i class="pi pi-cog text-orange-500"></i>
            Plan & Configuration
          </div>
        </template>
        <template #content>
          <div class="flex flex-col gap-2">
            <label class="font-medium text-gray-700">Subscription Plan *</label>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div
                v-for="plan in plans"
                :key="plan.value"
                class="p-4 rounded-xl border-2 cursor-pointer transition-all text-center"
                :class="form.plan === plan.value
                  ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-pink-50'
                  : 'border-gray-200 hover:border-orange-300 bg-white'"
                @click="form.plan = plan.value"
              >
                <i :class="plan.icon" class="text-2xl text-orange-500 mb-2"></i>
                <p class="font-semibold text-gray-800">{{ plan.label }}</p>
                <small class="text-gray-500">{{ plan.users }}</small>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- Branding (New mode only) -->
      <Card v-if="mode === 'new'">
        <template #title>
          <div class="flex items-center gap-2 text-gray-800">
            <i class="pi pi-image text-orange-500"></i>
            Branding
          </div>
        </template>
        <template #content>
          <div class="flex flex-col gap-2">
            <label for="image" class="font-medium text-gray-700">Logo URL (Optional)</label>
            <InputText
              id="image"
              v-model="form.image"
              placeholder="https://example.com/logo.png"
            />
          </div>
        </template>
      </Card>

      <!-- Import Section (Import mode only) -->
      <Card v-if="mode === 'import'">
        <template #title>
          <div class="flex items-center gap-2 text-gray-800">
            <i class="pi pi-file-import text-orange-500"></i>
            Import Data
          </div>
        </template>
        <template #content>
          <!-- File Upload -->
          <FileUpload
            v-if="!importFile"
            mode="basic"
            accept=".zip"
            :maxFileSize="500000000"
            chooseLabel="Choose ZIP file"
            class="w-full"
            customUpload
            @select="handleFileSelect"
          >
            <template #empty>
              <div class="flex flex-col items-center py-8 border-2 border-dashed
                          border-gray-300 rounded-xl hover:border-orange-400
                          transition-colors cursor-pointer">
                <i class="pi pi-cloud-upload text-5xl text-orange-500 mb-4"></i>
                <p class="text-gray-700 font-medium mb-1">Drag and drop a ZIP file here</p>
                <span class="text-sm text-gray-500">Accepts tenant export files (.zip)</span>
              </div>
            </template>
          </FileUpload>

          <!-- Selected File Display -->
          <div
            v-else
            class="flex items-center gap-4 p-4 bg-green-50 rounded-xl
                   border border-green-200"
          >
            <i class="pi pi-file-archive text-3xl text-green-600"></i>
            <div class="flex-1">
              <p class="font-medium text-gray-800">{{ importFile.name }}</p>
              <span class="text-sm text-gray-500">{{ formatFileSize(importFile.size) }}</span>
            </div>
            <Button
              icon="pi pi-times"
              severity="danger"
              text
              rounded
              @click="removeFile"
            />
          </div>

          <!-- Validation Result -->
          <Message
            v-if="validationResult && validationResult.valid"
            severity="success"
            :closable="false"
            class="mt-4"
          >
            <div class="flex flex-col gap-2">
              <span class="font-semibold">Valid export file</span>
              <div v-if="validationResult.stats" class="flex gap-6 text-sm">
                <span>
                  Collections: <strong>{{ validationResult.stats.collections }}</strong>
                </span>
                <span>
                  Documents: <strong>{{ validationResult.stats.estimatedDocuments }}</strong>
                </span>
                <span>Files: <strong>{{ validationResult.stats.fileCount }}</strong></span>
              </div>
            </div>
          </Message>

          <Message
            v-if="validationResult && !validationResult.valid"
            severity="error"
            :closable="false"
            class="mt-4"
          >
            <div class="flex flex-col gap-2">
              <span class="font-semibold">Invalid export file</span>
              <ul class="list-disc list-inside text-sm">
                <li v-for="(error, idx) in validationResult.errors" :key="idx">{{ error }}</li>
              </ul>
            </div>
          </Message>

          <Message
            v-if="validationResult?.warnings?.length"
            severity="warn"
            :closable="false"
            class="mt-4"
          >
            <ul class="list-disc list-inside text-sm">
              <li v-for="(warning, idx) in validationResult.warnings" :key="idx">{{ warning }}</li>
            </ul>
          </Message>

          <!-- Validate Button -->
          <Button
            v-if="importFile && !validationResult"
            :label="validating ? 'Validating...' : 'Validate Import'"
            :icon="validating ? 'pi pi-spin pi-spinner' : 'pi pi-shield'"
            :disabled="validating"
            severity="secondary"
            outlined
            class="w-full mt-4"
            @click="validateImport"
          />
        </template>
      </Card>

      <!-- Submit Actions -->
      <div class="flex justify-end gap-4 pt-4">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="cancel"
        />
        <Button
          :label="submitting
            ? (mode === 'import' ? 'Importing...' : 'Creating...')
            : (mode === 'import' ? 'Create & Import' : 'Create Tenant')"
          :icon="submitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
          :disabled="submitting || (mode === 'import' && (!importFile || !validationResult?.valid))"
          type="submit"
          class="bg-gradient-to-r from-pink-500 via-red-500 to-orange-500"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { platformApi } from '@/services/platformApi';
import Card from 'primevue/card';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import SelectButton from 'primevue/selectbutton';
import Tag from 'primevue/tag';
import FileUpload from 'primevue/fileupload';
import Message from 'primevue/message';

export default {
  name: 'CreateTenant',
  components: {
    Card,
    Button,
    InputText,
    Textarea,
    SelectButton,
    Tag,
    FileUpload,
    Message,
  },
  props: {
    isDarkTheme: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mode: 'new',
      submitting: false,
      validating: false,
      importFile: null,
      validationResult: null,
      form: {
        name: '',
        slug: '',
        description: '',
        /** Required by API for TENANT_CREATED audit (compliance). */
        reason: '',
        plan: 'standard',
        image: '',
        adminEmail: '',
        additionalAdminsInput: '',
        superuserEmailsInput: '',
      },
      errors: {},
      modeOptions: [
        { label: 'New Tenant', value: 'new', icon: 'pi pi-plus-circle' },
        { label: 'Import from ZIP', value: 'import', icon: 'pi pi-upload' },
      ],
      plans: [
        {
          value: 'free', label: 'Free', users: 'Up to 10 users', icon: 'pi pi-star',
        },
        {
          value: 'standard', label: 'Standard', users: 'Up to 100 users', icon: 'pi pi-star-fill',
        },
        {
          value: 'premium', label: 'Premium', users: 'Up to 500 users', icon: 'pi pi-star-fill',
        },
        {
          value: 'enterprise', label: 'Enterprise', users: 'Unlimited', icon: 'pi pi-building',
        },
      ],
    };
  },
  methods: {
    generateSlug() {
      if (this.form.name && !this.form.slug) {
        this.form.slug = this.form.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }
    },
    validate() {
      this.errors = {};

      if (!this.form.name.trim()) {
        this.errors.name = 'Organization name is required';
      }

      if (!this.form.slug.trim()) {
        this.errors.slug = 'Tenant slug is required';
      } else if (!/^[a-z0-9-]+$/.test(this.form.slug)) {
        this.errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
      }

      if (!this.form.adminEmail.trim()) {
        this.errors.adminEmail = 'Primary admin email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.adminEmail)) {
        this.errors.adminEmail = 'Please enter a valid email address';
      }

      if (this.mode === 'new') {
        const r = (this.form.reason || '').trim();
        if (!r) {
          this.errors.reason = 'Reason is required';
        } else if (r.length > 200) {
          this.errors.reason = 'Reason must be at most 200 characters';
        }
      }

      return Object.keys(this.errors).length === 0;
    },
    parseEmailList(input) {
      if (!input || !input.trim()) return [];
      return input
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    },
    async handleSubmit() {
      if (!this.validate()) return;

      this.submitting = true;
      try {
        if (this.mode === 'import') {
          await this.handleImportSubmit();
        } else {
          await this.handleNewSubmit();
        }

        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: `Tenant "${this.form.name}" created successfully`,
          life: 3000,
        });

        this.$router.push('/platform/tenants');
      } catch (error) {
        console.error('Failed to create tenant:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to create tenant',
          life: 5000,
        });
      } finally {
        this.submitting = false;
      }
    },
    async handleNewSubmit() {
      const adminEmails = this.parseEmailList(this.form.additionalAdminsInput);
      const superuserEmails = this.parseEmailList(this.form.superuserEmailsInput);

      const payload = {
        name: this.form.name,
        slug: this.form.slug,
        plan: this.form.plan,
        description: this.form.description || undefined,
        image: this.form.image || undefined,
        adminEmail: this.form.adminEmail,
        adminEmails: adminEmails.length > 0 ? adminEmails : undefined,
        superuserEmails: superuserEmails.length > 0 ? superuserEmails : undefined,
        reason: (this.form.reason || '').trim(),
      };

      await platformApi.createTenant(payload);
    },
    async handleImportSubmit() {
      if (!this.importFile || !this.validationResult?.valid) {
        throw new Error('Please validate the import file first');
      }

      const formData = new FormData();
      formData.append('file', this.importFile);
      formData.append('slug', this.form.slug);
      formData.append('name', this.form.name);
      formData.append('adminEmail', this.form.adminEmail);
      formData.append('plan', this.form.plan);

      await platformApi.importTenant(formData);
    },
    handleFileSelect(event) {
      const file = event.files?.[0];
      if (file && file.name.endsWith('.zip')) {
        this.importFile = file;
        this.validationResult = null;
      }
    },
    removeFile() {
      this.importFile = null;
      this.validationResult = null;
    },
    async validateImport() {
      if (!this.importFile) return;

      this.validating = true;
      try {
        const formData = new FormData();
        formData.append('file', this.importFile);
        this.validationResult = await platformApi.validateImport(formData);
      } catch (error) {
        console.error('Validation failed:', error);
        this.validationResult = {
          valid: false,
          errors: ['Failed to validate file. Please try again.'],
          warnings: [],
        };
      } finally {
        this.validating = false;
      }
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    cancel() {
      this.$router.push('/platform/tenants');
    },
  },
};
</script>

<style scoped>
/* ========== Dark Theme Overrides ========== */
.create-tenant.dark-theme {
  color: #f5f5f5;
}

/* SelectButton dark mode - only background adjustments */
.create-tenant.dark-theme :deep(.p-selectbutton) {
  background: #2a2a24;
  border-color: #444;
}

.create-tenant.dark-theme :deep(.p-togglebutton:not(.p-togglebutton-checked)) {
  background: transparent;
  color: #a0a0a0;
  border-color: transparent;
}

.create-tenant.dark-theme :deep(.p-togglebutton:not(.p-togglebutton-checked):hover) {
  background: #3a3a34;
  color: #e0e0e0;
}

.create-tenant.dark-theme .text-gray-800 {
  color: #f5f5f5 !important;
}

.create-tenant.dark-theme .text-gray-700 {
  color: #e0e0e0 !important;
}

.create-tenant.dark-theme .text-gray-600 {
  color: #c0c0c0 !important;
}

.create-tenant.dark-theme .text-gray-500 {
  color: #a0a0a0 !important;
}

.create-tenant.dark-theme .bg-white {
  background-color: #2a2a24 !important;
}

.create-tenant.dark-theme .bg-gray-50 {
  background-color: #1E2019 !important;
}

.create-tenant.dark-theme .border-gray-200,
.create-tenant.dark-theme .border-gray-300 {
  border-color: rgba(228, 130, 26, 0.2) !important;
}

.create-tenant.dark-theme .hover\:border-orange-300:hover {
  border-color: rgba(228, 130, 26, 0.5) !important;
}

.create-tenant.dark-theme .bg-green-50 {
  background-color: rgba(34, 197, 94, 0.15) !important;
}

.create-tenant.dark-theme .border-green-200 {
  border-color: rgba(34, 197, 94, 0.3) !important;
}

/* PrimeVue dark overrides */
.create-tenant.dark-theme :deep(.p-card) {
  background: #2a2a24;
  color: #f5f5f5;
  border: 1px solid rgba(228, 130, 26, 0.2);
}

.create-tenant.dark-theme :deep(.p-card .p-card-title) {
  color: #f5f5f5;
}

.create-tenant.dark-theme :deep(.p-inputtext) {
  background: #1E2019;
  color: #f5f5f5;
  border-color: rgba(228, 130, 26, 0.3);
}

.create-tenant.dark-theme :deep(.p-inputtext:focus) {
  border-color: #E4821A;
}

.create-tenant.dark-theme :deep(.p-textarea) {
  background: #1E2019;
  color: #f5f5f5;
  border-color: rgba(228, 130, 26, 0.3);
}

.create-tenant.dark-theme :deep(.p-fileupload-content) {
  background: #1E2019;
  border-color: rgba(228, 130, 26, 0.3);
}

/* Plan cards */
.create-tenant.dark-theme .border-orange-500 {
  border-color: #E4821A !important;
}

.create-tenant.dark-theme .from-orange-50 {
  --tw-gradient-from: rgba(228, 130, 26, 0.15) !important;
}

.create-tenant.dark-theme .to-pink-50 {
  --tw-gradient-to: rgba(236, 72, 153, 0.1) !important;
}
</style>
